import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { BusinessRole } from '@mondaygpt/constants'
import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  dropdownItemDarkClass,
} from 'components/DropdownMenu'
import { cn, reloadToRoot } from 'lib/utils'
import { useAnalytics } from 'hooks'
import { Check, ChevronsUpDown, LogOut, Plus } from 'lucide-react'
import { selectCurrentBusinessRole } from 'modules/authentication'
import {
  selectBusinessEntitlement,
  selectShouldShowUpgrade,
} from 'modules/billing/slices/credit-balance.slice'
import {
  leaveCurrentBusinessAction,
  selectBusinesses,
  selectCurrentBusiness,
  switchBusinessAction,
} from 'modules/business'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectBrandLogo, useBrandKitStore } from '../store/brand-kit-store'

const initialsOf = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

export function BusinessSwitcher() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const businesses = useAppSelector(selectBusinesses)
  const currentBusiness = useAppSelector(selectCurrentBusiness)
  const showUpgrade = useAppSelector(selectShouldShowUpgrade)
  const entitlement = useAppSelector(selectBusinessEntitlement)
  const brandKitLogo = useBrandKitStore(selectBrandLogo)
  const businessRole = useAppSelector(selectCurrentBusinessRole)

  const [leaveOpen, setLeaveOpen] = useState(false)
  const [leaveBusy, setLeaveBusy] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)

  // JWT exposes `brole` so the right action shows immediately on mount with
  // no round-trip. Owners manage (delete) their business from the billing
  // settings page; the switcher only offers Leave to non-owner members.
  // Falsy role ⇒ pending business (no current scope) or a legacy token
  // (pre-`brole`) — hide the section in both cases.
  const showLeave = !!currentBusiness && !!businessRole && businessRole !== BusinessRole.Owner

  const handleSwitch = useCallback(
    async (businessId: string) => {
      if (businessId === currentBusiness?.id) return
      const targetBiz = businesses.find(b => b.id === businessId)
      track(AnalyticsEvents.Sidebar.SIDEBAR_BUSINESS_SWITCHED, {
        business_id: businessId,
        business_name: targetBiz?.name || '',
      })
      try {
        await dispatch(switchBusinessAction({ businessId })).unwrap()
        reloadToRoot()
      } catch (err) {
        console.error('Failed to switch business:', err)
      }
    },
    [dispatch, currentBusiness?.id, businesses, track]
  )

  const handleAddBusiness = useCallback(() => {
    track(AnalyticsEvents.Sidebar.SIDEBAR_CREATE_BUSINESS_TAPPED)
    navigate('/businesses/new')
  }, [navigate, track])

  const closeLeave = useCallback(() => {
    if (leaveBusy) return
    setLeaveOpen(false)
    setLeaveError(null)
  }, [leaveBusy])

  const confirmLeave = useCallback(async () => {
    setLeaveBusy(true)
    setLeaveError(null)
    try {
      await dispatch(leaveCurrentBusinessAction()).unwrap()
      reloadToRoot()
    } catch (err) {
      console.error('Failed to leave business:', err)
      const payload = err as { message?: string }
      setLeaveError(payload?.message || 'Something went wrong. Try again.')
      setLeaveBusy(false)
    }
  }, [dispatch])

  const initials = useMemo(() => initialsOf(currentBusiness?.name || 'T'), [currentBusiness?.name])

  // Brand-kit store logo (current business, freshest) first, then the
  // list-hydrated logoUrl so the header has a logo before the kit loads.
  const headerLogo = brandKitLogo || currentBusiness?.logoUrl

  const businessDisplay = (
    <>
      {headerLogo ? (
        <img
          src={headerLogo}
          alt={currentBusiness?.name}
          className="h-8 w-8 flex-shrink-0 rounded-[8px] border border-line-bright bg-white object-contain p-0.5"
        />
      ) : (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] border border-line-bright bg-bg-3 text-[10px] font-semibold text-paper-dim">
          {initials}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-paper">
          {currentBusiness?.name || 'Select Business'}
        </p>
        <p className="mono truncate text-[10px] tracking-[0.05em] text-paper-faint uppercase">
          {showUpgrade ? 'Free Plan' : `${entitlement?.tier || 'Pro'} Plan`}
        </p>
      </div>
    </>
  )

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex w-full items-center gap-3 rounded-[8px] px-3 py-3 text-left transition-colors hover:bg-bg-3"
            data-testid="business-switcher-trigger"
          >
            {businessDisplay}
            <ChevronsUpDown size={14} className="flex-shrink-0 text-paper-faint" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent variant="dark" align="start" className="w-64" sideOffset={4}>
          <DropdownMenuLabel className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Businesses
          </DropdownMenuLabel>

          {businesses.map(biz => (
            <DropdownMenuItem
              key={biz.id}
              onSelect={() => handleSwitch(biz.id)}
              className={cn('cursor-pointer gap-2', dropdownItemDarkClass)}
              data-testid={`business-switcher-item-${biz.id}`}
            >
              {biz.logoUrl ? (
                <img
                  src={biz.logoUrl}
                  alt=""
                  className="h-6 w-6 flex-shrink-0 rounded-[6px] border border-line bg-white object-contain p-0.5"
                />
              ) : (
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[6px] border border-line bg-bg-2 text-[10px] font-semibold text-paper-faint">
                  {initialsOf(biz.name)}
                </div>
              )}
              <span className="min-w-0 flex-1 truncate text-[13px]">{biz.name}</span>
              {biz.id === currentBusiness?.id && (
                <Check size={14} className="flex-shrink-0 text-acid" />
              )}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator className="my-1 h-px bg-line" />

          <DropdownMenuItem
            onSelect={handleAddBusiness}
            className={cn('cursor-pointer gap-2', dropdownItemDarkClass)}
            data-testid="business-switcher-add"
          >
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[6px] border border-dashed border-line text-paper-faint">
              <Plus size={12} />
            </div>
            <span className="text-[13px]">Add business</span>
          </DropdownMenuItem>

          {showLeave && (
            <>
              <DropdownMenuSeparator className="my-1 h-px bg-line" />
              <DropdownMenuItem
                onSelect={() => {
                  setLeaveError(null)
                  setLeaveOpen(true)
                }}
                className={cn(
                  'cursor-pointer gap-2 text-[color:var(--alert-error-text)]',
                  dropdownItemDarkClass
                )}
                data-testid="business-switcher-leave"
              >
                <LogOut size={14} className="flex-shrink-0" />
                <span className="text-[13px]">Leave business</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={leaveOpen}
        onOpenChange={open => {
          if (!open) closeLeave()
        }}
      >
        <DialogContent variant="dark" size="sm" className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[16px] font-semibold text-paper">
              {`Leave "${currentBusiness?.name ?? 'this business'}"?`}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-paper-dim">
              You lose access to this business. The owner can re-invite you. Your personal account
              stays active.
            </DialogDescription>
          </DialogHeader>
          {leaveError && (
            <p className="text-[12px] text-[color:var(--alert-error-text)]">{leaveError}</p>
          )}
          <DialogFooter className="mt-2 flex-row justify-end gap-2">
            <Button
              type="button"
              variant="t3-tertiary"
              size="t3-md"
              onClick={closeLeave}
              disabled={leaveBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="t3-destructive"
              size="t3-md"
              onClick={confirmLeave}
              disabled={leaveBusy}
              data-testid="business-switcher-destructive-confirm"
            >
              {leaveBusy ? 'Leaving…' : 'Leave business'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
